import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AuthModal from "@/auth/AuthModal";
import AccountManager from "@/auth/AccountManager";
import { useAuth } from "@/auth/AuthContext";

/** Where a sign-in may return to: a path here, or a page on Forma. Mirrors safeNext in functions/_lib/auth.js. */
function safeNext(next) {
  if (!next) return "";
  if (next.startsWith("/") && !next.startsWith("//")) return next;
  try {
    const u = new URL(next);
    return ["https://forma.malickecase.com", "https://trinity.malickecase.com"].includes(u.origin) ? u.toString() : "";
  } catch {
    return "";
  }
}

/**
 * /signin?next=… — one account for malickecase.com and Forma. Signed out: the sign-in dialog, then back to `next`.
 * /account — the account page (name, password, delete), with a way back to `next`.
 */
export default function SignInPage({ account = false }) {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const { user, loading } = useAuth();

  useEffect(() => { document.title = account ? "Your account" : "Sign in"; }, [account]);
  useEffect(() => {
    if (!account && !loading && user && next) window.location.replace(next);
  }, [account, loading, user, next]);

  if (loading) return null;
  return (
    <div style={{ minHeight: "100vh", background: "#F5F3EF", color: "#1A1F26", fontFamily: "'Inter', system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 20px" }}>
      <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
        <p style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#5A6470" }}>malickecase.com</p>
        <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em", margin: "8px 0 12px" }}>{account ? "Your account" : "Sign in"}</h1>
        <p style={{ fontSize: 14, color: "#5A6470", marginBottom: 24 }}>One account for the journal, Forma and Trinity.</p>
        {user ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
            <p style={{ fontSize: 14 }}>Signed in as <strong>{user.name || user.email}</strong></p>
            <div className="blog-theme" data-blog-theme="light"><AccountManager /></div>
            {next ? <a href={next} style={{ fontSize: 14, color: "#0E3A3A" }}>Back to {next.startsWith("https://forma.") ? "Forma" : next.startsWith("https://trinity.") ? "Trinity" : "the site"} →</a> : <Link to="/blog" style={{ fontSize: 14, color: "#0E3A3A" }}>Go to the journal →</Link>}
          </div>
        ) : (
          <AuthModal
            open
            next={account ? `/account${next ? `?next=${encodeURIComponent(next)}` : ""}` : next || "/blog"}
            title="Sign in"
            onClose={(u) => { if (u && account) return; window.location.replace(u ? next || "/blog" : next || "/"); }}
          />
        )}
      </div>
    </div>
  );
}
